import { ERROR_CODES } from '@/constants/error/errorCode'
import type { UploadFileData } from '@/types/aeroUpload/aeroUpload'
import type {
  CommonParam,
  GetPresignedUrlParam,
  GetPresignedUrlResponseResult,
  MultiPart,
  UploadChunkWithProgressResponse
} from '@/types/api/dup/fileUpload/fileUploadApi'
import { GlobalError } from '@/utils/globalError/GlobalError'

// ファイルを一定サイズにチャンク分けしてアップロード
const CHUNK_SIZE = 16 * 1024 * 1024
//リトライ数
const MAX_RETRIES = 3
//並列数の設定。ブラウザの同時接続数限界は6
const CONCURRENCY = 4

/**
 * ファイルをチャンク分けして並列アップロードを行う
 * @param fileData ファイル
 * @param commonParam ファイルアップロード共通パラメーター
 * @param uploadKey アップロードキー
 * @param uploadId アップロードID
 * @param getPresignedUrl 署名付きURL取得関数
 * @param uploadChunkWithProgress アップロード進捗管理関数
 * @returns アップロード結果
 */
export const parallelChunksUpload = async (
  fileData: UploadFileData,
  commonParam: CommonParam,
  uploadKey: string,
  uploadId: string,
  getPresignedUrl: (param: GetPresignedUrlParam) => Promise<GetPresignedUrlResponseResult>,
  uploadChunkWithProgress: (
    url: string,
    chunk: Blob,
    onProgress: (loaded: number, total: number) => void,
    fileKey: string
  ) => Promise<UploadChunkWithProgressResponse>
) => {
  const totalParts = Math.ceil(fileData.file.size / CHUNK_SIZE)
  const multipartList: MultiPart[] = []
  let uploadedBytes = 0
  const totalBytes = fileData.file.size
  const uploadedBytesMap = new Map<number, number>()
  //処理中のパート
  let currentPart = 1

  //進捗管理用
  function updateProgress(partNumber: number, loaded: number) {
    const prevLoaded = uploadedBytesMap.get(partNumber) || 0
    //差分
    const delta = loaded - prevLoaded
    if (delta > 0) {
      uploadedBytesMap.set(partNumber, loaded)
      uploadedBytes += delta
      fileData.progress = Math.floor((uploadedBytes / totalBytes) * 100)
    }
  }

  // チャンクアップロード処理（1パート分）
  async function uploadPart(partNumber: number) {
    const start = (partNumber - 1) * CHUNK_SIZE
    const end = Math.min(fileData.file.size, start + CHUNK_SIZE)
    const chunk = fileData.file.slice(start, end)
    let attempts = 0

    while (true) {
      attempts++
      try {
        // 署名付きURL取得してバイナリデータをアップロード
        const { url: presignedUrl } = await getPresignedUrl({
          ...commonParam,
          key: uploadKey,
          uploadId,
          partNumber
        })

        const progressCallback = (loaded: number) => {
          updateProgress(partNumber, loaded)
        }

        const { etag } = await uploadChunkWithProgress(
          presignedUrl,
          chunk,
          progressCallback,
          fileData.key
        )

        return {
          ETag: etag.replace(/"/g, ''),
          PartNumber: partNumber
        }
      } catch (error: unknown) {
        if (attempts >= MAX_RETRIES) {
          if (error instanceof Error) {
            //fileUploadApiでエラーが起きた場合
            const newError = new Error(
              `Part ${partNumber} upload failed after ${attempts} attempts: ${String(error)}`
            )
            newError.stack = error.stack || newError.stack
            throw newError
          } else {
            throw new GlobalError([ERROR_CODES._811010_API_EXECUTION_FAILED], {
              message: `Part ${partNumber} upload failed after ${attempts} attempts: ${String(error)}`
            })
          }
        }
        // リトライ前に待機
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts))
      }
    }
  }

  // workerを並列に動かし、順にチャンクを消化
  async function worker() {
    while (true) {
      const partNumber = currentPart
      currentPart++
      if (partNumber > totalParts) break

      const part = await uploadPart(partNumber)
      multipartList.push(part)
    }
  }

  //ワーカーリストの作成
  const workers = Array(CONCURRENCY)
    .fill(0)
    .map(() => worker())

  await Promise.all(workers)

  //パーツリストをファイル結合用にパーツ順で並び替え
  multipartList.sort((a, b) => a.PartNumber - b.PartNumber)

  return multipartList
}
